import { Check, Copy, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Dialog, FOCUSABLE_SELECTOR } from "~/components/Dialog";
import {
  SheetHeader,
  VerificationGhostButton,
  VerificationPrimaryButton,
} from "~/components/verification-primitives";
import { restoreKeyBackupWithRecoveryKey, type Client, type RecoveryViewModel } from "~/lib/matrix";
import { isMobileApp } from "~/lib/platform";
import { ICON_STROKE } from "~/lib/icon";

export type KeyBackupSetupMode =
  | {
      kind: "save";
      /** Pre-formatted "AAAA BBBB …" recovery key to display once. */
      recoveryKey: string;
      onConfirm: () => void;
      onCancel?: () => void;
    }
  | {
      kind: "restore";
      /** Starts the server-side restore. Resolves with the number of
       *  keys recovered when known; rejects with a message the UI
       *  renders in the error helper. */
      onRestore: (recoveryKey: string) => Promise<{ restoredCount: number | null }>;
      onVerifyDevice?: (deviceId: string, method?: "qr" | "emoji") => Promise<void>;
      onSkip: () => void;
      onDone: () => void;
      dismissPolicy?: "allow" | "skip-only";
      recoveryView?: RecoveryViewModel | null;
      deviceRestoreStatus?: "idle" | "verified" | "restoring" | "restored" | "failed";
      deviceRestoreError?: string | null;
      /** When non-null, the dialog renders the in-progress state with
       *  a running count. Restores are fast in the stub but the
       *  real SDK streams progress as sessions unlock. */
      progress?: { restored: number; total: number } | null;
    };

export function buildKeyBackupRestoreMode(
  client: Client,
  onDone: () => void,
  onSkip: () => void = onDone,
  dismissPolicy: "allow" | "skip-only" = "allow",
): Extract<KeyBackupSetupMode, { kind: "restore" }> {
  return {
    kind: "restore",
    onRestore: (key) => restoreKeyBackupWithRecoveryKey(client, key),
    onSkip,
    onDone,
    dismissPolicy,
  };
}

export interface KeyBackupSetupProps {
  mode: KeyBackupSetupMode;
}

export function KeyBackupSetup({ mode }: KeyBackupSetupProps) {
  const onDismiss = useCallback(() => {
    if (mode.kind === "restore" && (mode.dismissPolicy ?? "allow") === "allow") mode.onSkip();
  }, [mode]);

  return (
    <Dialog
      onClose={onDismiss}
      labelledBy="key-backup-title"
      grabber={false}
      width="520px"
      panelStyle={{
        maxWidth: "calc(100vw - var(--chat-space-8))",
        borderRadius: "var(--chat-radius-lg)",
      }}
    >
      {mode.kind === "save" ? <SaveBody mode={mode} /> : <RestoreBody mode={mode} />}
    </Dialog>
  );
}

/* ── Save flow ─────────────────────────────────────────────────── */

function SaveBody({ mode }: { mode: Extract<KeyBackupSetupMode, { kind: "save" }> }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const copyTimerRef = useRef<number | null>(null);

  // Reset the "Copied" pill after 2s. The pill is a transient state —
  // leaving it on forever would imply the key is on the clipboard
  // indefinitely, which modern clipboard managers can revoke.
  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    // Clipboard API is gated behind a secure context; fall back to a
    // hidden textarea + execCommand so the dialog still works in
    // embedded WebViews where `navigator.clipboard` is missing.
    //
    // The fallback creates a DOM node holding the recovery key — the
    // most sensitive string this app ever surfaces. `removeChild`
    // MUST run even if `execCommand` throws, or the key lingers in
    // the document. Wrapping the append/select/copy in try/finally
    // guarantees cleanup independent of the copy outcome.
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(mode.recoveryKey);
        copied = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = mode.recoveryKey;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.setAttribute("readonly", "");
        ta.setAttribute("aria-hidden", "true");
        document.body.appendChild(ta);
        try {
          ta.select();
          copied = document.execCommand("copy");
        } finally {
          document.body.removeChild(ta);
        }
      }
    } catch (err) {
      console.warn("Copy failed", err);
    }
    if (!copied) return;
    setCopied(true);
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
    // Copy no longer auto-enables "I've saved it" — the user might
    // paste into a temporary note by accident, close the dialog, and
    // lose the key. Keeping the checkbox as an explicit, intentional
    // gesture is the only way this confirmation means something.
  }, [mode.recoveryKey]);

  return (
    <>
      <DialogHeader title="Save your recovery key" />
      {/* The header already ends in space-6; a second top inset would
          double the gap under the title. */}
      <div style={{ padding: "0 var(--chat-space-8) var(--chat-space-4)" }}>
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          If you lose access to all your devices, this is the only way to unlock your message
          history.
        </p>
        {/* The key is its own copy action: the glyph at the trailing edge
            says so, and becomes a check once it is on the clipboard. */}
        <button
          type="button"
          data-testid="recovery-key"
          className="chat-button"
          aria-label="Copy recovery key"
          onClick={handleCopy}
          style={{
            position: "relative",
            display: "block",
            width: "100%",
            marginTop: "var(--chat-space-4)",
            padding:
              "var(--chat-space-3) var(--chat-space-10, 40px) var(--chat-space-3) var(--chat-space-3)",
            border: "none",
            textAlign: "left",
            background: "var(--chat-surface-panel)",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-mono)",
            fontSize: "var(--chat-type-mono-size)",
            lineHeight: "var(--chat-type-mono-line)",
            overflowWrap: "anywhere",
            cursor: "pointer",
          }}
        >
          {/* Four groups a line: two even rows rather than a ragged wrap. */}
          <span style={{ display: "block", maxWidth: "19.5ch" }}>{mode.recoveryKey}</span>
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%",
              right: "var(--chat-space-3)",
              transform: "translateY(-50%)",
              display: "inline-flex",
              color: "var(--chat-fg-secondary)",
            }}
          >
            {copied ? (
              <Check size={14} strokeWidth={ICON_STROKE} />
            ) : (
              <Copy size={14} strokeWidth={ICON_STROKE} />
            )}
          </span>
        </button>
        <span role="status" className="sr-only">
          {copied ? "Copied" : ""}
        </span>
      </div>
      {/* The confirmation and the action it unlocks read as one group. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-3)",
          padding: "0 var(--chat-space-8) var(--chat-space-6)",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-2)",
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            className="chat-button"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            style={{ accentColor: "var(--chat-accent)" }}
          />
          I&apos;ve saved my recovery key somewhere safe
        </label>
        <VerificationPrimaryButton
          size="md"
          block
          disabled={!acknowledged}
          onClick={mode.onConfirm}
        >
          I&apos;ve saved it
        </VerificationPrimaryButton>
      </div>
    </>
  );
}

/* ── Restore flow ──────────────────────────────────────────────── */

function RestoreBody({ mode }: { mode: Extract<KeyBackupSetupMode, { kind: "restore" }> }) {
  const hasOverview = mode.recoveryView != null;
  const [phase, setPhase] = useState<"overview" | "recovery-key">(() =>
    hasOverview ? "overview" : "recovery-key",
  );
  const startedWithoutOverviewRef = useRef(!hasOverview);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ restoredCount: number | null } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!hasOverview || !startedWithoutOverviewRef.current || value.trim().length > 0) return;
    startedWithoutOverviewRef.current = false;
    setPhase("overview");
  }, [hasOverview, value]);

  // A restore view swap unmounts the focused control and drops focus on <body>;
  // re-pull focus into the panel whenever it escapes.
  useEffect(() => {
    const panel = document.querySelector<HTMLElement>(
      '[role="dialog"][aria-labelledby="key-backup-title"]',
    );
    if (!panel) return;
    const refocus = () => {
      if (panel.contains(document.activeElement)) return;
      // Fall back to the panel itself (it is focusable) for views with no
      // interactive element, so focus never lands on <body>.
      const target = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? panel;
      target.focus();
    };
    refocus();
    const observer = new MutationObserver(refocus);
    observer.observe(panel, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setError("Enter your recovery key to continue.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await mode.onRestore(trimmed);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't unlock. Try again.");
    } finally {
      setSubmitting(false);
    }
  }, [mode, value]);

  if (result) {
    return (
      <>
        <DialogHeader title="Messages unlocked" />
        <div
          style={{
            padding: "var(--chat-space-8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--chat-space-3)",
            textAlign: "center",
          }}
        >
          <Check
            size={32}
            strokeWidth={ICON_STROKE}
            aria-hidden="true"
            style={{ color: "var(--chat-fg-secondary)" }}
          />
          <p
            style={{
              margin: 0,
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
            }}
          >
            {result.restoredCount === null || result.restoredCount === 0
              ? "Your messages are ready to read."
              : `Unlocked ${result.restoredCount} ${
                  result.restoredCount === 1 ? "message" : "messages"
                }.`}
          </p>
        </div>
        <DialogFooter>
          <VerificationPrimaryButton size="md" block onClick={mode.onDone}>
            Done
          </VerificationPrimaryButton>
        </DialogFooter>
      </>
    );
  }

  const inProgress =
    submitting || (mode.progress != null && mode.progress.restored < mode.progress.total);

  if (
    mode.deviceRestoreStatus &&
    mode.deviceRestoreStatus !== "idle" &&
    mode.deviceRestoreStatus !== "failed"
  ) {
    return <DeviceRestoreProgress mode={mode} />;
  }

  if (phase !== "recovery-key" && !result) {
    return <RestoreOverview mode={mode} onPhase={setPhase} />;
  }

  return (
    <>
      <DialogHeader title="Unlock your messages" />
      <div
        style={{
          padding:
            "var(--chat-space-5) var(--chat-space-8) var(--chat-space-6) var(--chat-space-8)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          Enter your recovery key to unlock older messages on this device.
        </p>
        <input
          type="text"
          spellCheck={false}
          autoComplete="off"
          placeholder="Enter recovery key"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          aria-invalid={error != null}
          aria-describedby={error ? "key-backup-error" : undefined}
          className={
            error
              ? "block w-full border-0 border-b bg-transparent outline-none border-b-[var(--chat-state-danger)]"
              : "block w-full border-0 border-b bg-transparent outline-none border-b-[var(--chat-surface-border)] focus:border-b-[var(--chat-fg-secondary)]"
          }
          style={{
            marginTop: "var(--chat-space-4)",
            padding: "0 0 var(--chat-space-1)",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-mono)",
            fontSize: "var(--chat-type-mono-size)",
            lineHeight: "var(--chat-type-mono-line)",
          }}
        />
        {error && (
          <p
            id="key-backup-error"
            role="alert"
            style={{
              margin: "var(--chat-space-2) 0 0 0",
              color: "var(--chat-state-danger)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            {error}
          </p>
        )}
        {inProgress && mode.progress && (
          <p
            style={{
              margin: "var(--chat-space-3) 0 0 0",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
            }}
          >
            Unlocked {mode.progress.restored} of {mode.progress.total} keys
          </p>
        )}
      </div>
      <DialogFooter>
        <VerificationGhostButton
          size="md"
          onClick={() => {
            if (phase === "recovery-key" && hasOverview) {
              setPhase("overview");
              setError(null);
              return;
            }
            mode.onSkip();
          }}
          disabled={inProgress}
        >
          {phase === "recovery-key" && hasOverview ? "Back" : "Not now"}
        </VerificationGhostButton>
        <VerificationPrimaryButton
          size="md"
          onClick={handleSubmit}
          disabled={inProgress || value.trim().length === 0}
        >
          {inProgress ? "Unlocking" : "Unlock"}
        </VerificationPrimaryButton>
      </DialogFooter>
    </>
  );
}

function RestoreOverview({
  mode,
  onPhase,
}: {
  mode: Extract<KeyBackupSetupMode, { kind: "restore" }>;
  onPhase: (phase: "overview" | "recovery-key") => void;
}) {
  const recoveryView = mode.recoveryView ?? null;
  const verifiesWithDevice = recoveryView?.primaryAction.kind === "verify-device";

  async function runPrimary() {
    const action = recoveryView?.primaryAction;
    if (!action || action.kind === "recovery-key") {
      onPhase("recovery-key");
      return;
    }
    await mode.onVerifyDevice?.(
      action.device.deviceId,
      isMobileApp || !recoveryView.canUseEmoji ? "qr" : "emoji",
    );
  }

  return (
    <>
      <DialogHeader title="Unlock your messages" onClose={mode.onSkip} />
      {mode.deviceRestoreStatus === "failed" && mode.deviceRestoreError && (
        <p role="alert" style={{ ...smallTextStyle, margin: 0, padding: "0 var(--chat-space-8)" }}>
          {mode.deviceRestoreError}
        </p>
      )}
      <RestoreOverviewActions>
        <VerificationPrimaryButton size="md" block onClick={() => void runPrimary()}>
          {verifiesWithDevice ? "Approve on another device" : "Enter recovery key"}
        </VerificationPrimaryButton>
        {verifiesWithDevice ? (
          <VerificationGhostButton size="md" block onClick={() => onPhase("recovery-key")}>
            Use recovery key instead
          </VerificationGhostButton>
        ) : null}
      </RestoreOverviewActions>
      <p
        style={{
          ...smallTextStyle,
          margin: 0,
          padding: "0 var(--chat-space-8) var(--chat-space-2)",
        }}
      >
        Closing leaves your older messages locked. You can unlock them later with your recovery key
        or from another device.
      </p>
    </>
  );
}

/** "Messages unlocked" confirms, then gets out of the way. */
export const RESTORED_AUTO_CLOSE_MS = 2000;

function DeviceRestoreProgress({
  mode,
}: {
  mode: Extract<KeyBackupSetupMode, { kind: "restore" }>;
}) {
  const status = mode.deviceRestoreStatus;
  const isRestored = status === "restored";
  // Parents pass a fresh onDone each render; a ref keeps re-renders from re-arming the timer.
  const onDoneRef = useRef(mode.onDone);
  onDoneRef.current = mode.onDone;

  useEffect(() => {
    if (!isRestored) return;
    const timer = setTimeout(() => onDoneRef.current(), RESTORED_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [isRestored]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "var(--chat-space-8)",
        padding: "var(--chat-space-10) var(--chat-space-8) var(--chat-space-8)",
      }}
    >
      <h2 id="key-backup-title" className="chat-visually-hidden">
        Unlock messages
      </h2>
      <div
        role="status"
        aria-live="polite"
        style={{
          display: "flex",
          minHeight: "var(--chat-space-16)",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--chat-space-3)",
          textAlign: "center",
        }}
      >
        {isRestored ? (
          <Check
            size={32}
            strokeWidth={ICON_STROKE}
            aria-hidden="true"
            style={{ color: "var(--chat-state-success)" }}
          />
        ) : (
          <LoaderCircle
            size={32}
            strokeWidth={ICON_STROKE}
            className="chat-timeline-spinner"
            aria-hidden="true"
          />
        )}
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-lg-size)",
            lineHeight: "var(--chat-type-display-lg-line)",
            fontWeight: 500,
          }}
        >
          {isRestored ? "Messages unlocked" : "Unlocking messages"}
        </p>
      </div>
    </div>
  );
}

/* ── Shared dialog primitives ─────────────────────────────────── */

const smallTextStyle: React.CSSProperties = {
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
};

function DialogHeader({ title, onClose }: { title: string; onClose?: () => void }) {
  return (
    <div style={{ padding: "var(--chat-space-6) var(--chat-space-8) var(--chat-space-6)" }}>
      <SheetHeader id="key-backup-title" title={title} onClose={onClose} />
    </div>
  );
}

function DialogFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: 72,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: "flex-start",
        gap: "var(--chat-space-3)",
        padding: "0 var(--chat-space-8) var(--chat-space-6)",
      }}
    >
      {children}
    </div>
  );
}

function RestoreOverviewActions({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "var(--chat-space-3)",
        padding: "0 var(--chat-space-8) var(--chat-space-4)",
      }}
    >
      {children}
    </div>
  );
}
